import { IonBackButton, IonButton, IonButtons, IonCard, IonCardContent, IonCardHeader, IonCardTitle, IonCol, IonContent, IonGrid, IonHeader, IonIcon, IonInput, IonItem, IonLabel, IonList, IonModal, IonPage, IonRow, IonSearchbar, IonSpinner, IonTitle, IonToggle, IonToolbar, useIonViewWillEnter, useIonViewWillLeave } from '@ionic/react';

import './Pump.css';
import { useEffect, useState, useRef } from 'react';
import { AxiosResponse } from 'axios';
import getApi from '../../api';
import EnvironmentBadge from '../../components/EnvironmentBadge';
import { useParams } from 'react-router';
import Swal from 'sweetalert2';
import { addCircleOutline, closeOutline, trashOutline } from 'ionicons/icons';

const PLCParameters: React.FC = () => {
    const { id, userId } = useParams<{ id: string, userId: string }>();
    const [pump, setPump] = useState([] as any);
    const [isSaving, setIsSaving] = useState<string | null>(null);
    const [editValues, setEditValues] = useState<{ [key: string]: string }>({});
    const [showSlaveModal, setShowSlaveModal] = useState(false);
    const [allPumps, setAllPumps] = useState([] as any);
    const [searchText, setSearchText] = useState('');
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    const fetchData = async (isInitial = false) => {
        getApi().get(`/bbe/userpumps/${id}/`).then((response: AxiosResponse) => {
            setPump(response.data);
            // Initialize edit values only on first load or if not saving
            if (isInitial) {
                setEditValues({
                    slave_1_start_frequency: response.data.slave_1_start_frequency?.toString() || '0',
                    slave_1_start_time: response.data.slave_1_start_time?.toString() || '0',
                    slave_1_stop_frequency: response.data.slave_1_stop_frequency?.toString() || '0',
                    slave_1_stop_time: response.data.slave_1_stop_time?.toString() || '0',
                    allowed_break_flowguard_time: response.data.allowed_break_flowguard_time?.toString() || '0',
                    allowed_break_lowguard_time: response.data.allowed_break_lowguard_time?.toString() || '0',
                    allowed_break_highguard_time: response.data.allowed_break_highguard_time?.toString() || '0',
                    allowed_break_maxpressure_time: response.data.allowed_break_maxpressure_time?.toString() || '0',
                    highguard: response.data.highguard?.toString() || '0',
                    lowguard: response.data.lowguard?.toString() || '0',
                    prestart_time: response.data.prestart_time?.toString() || '0',
                    prestart_frequens: response.data.prestart_frequens?.toString() || '0',
                    prestart_pressure_threshold: response.data.prestart_pressure_threshold?.toString() || '0',
                    continuous_prefill: response.data.continuous_prefill
                });
            }
        });
    }

    const fetchAllPumps = async () => {
        getApi().get(`/bbe/userpumps/?user=${userId}`).then((response: AxiosResponse) => {
            setAllPumps(Array.isArray(response.data) ? response.data : response.data.results || []);
        });
    }

    useEffect(() => {
        if (showSlaveModal) {
            fetchAllPumps();
        }
    }, [showSlaveModal]);

    const addSlave = async (slaveId: number) => {
        const currentSlaves = Array.isArray(pump.slaves) ? pump.slaves.map((s: any) => s.id || s) : [];
        if (currentSlaves.includes(slaveId)) return;

        const newSlaves = [...currentSlaves, slaveId];
        saveSlaves(newSlaves, `adding-${slaveId}`);
    };

    const removeSlave = async (slaveId: number) => {
        const currentSlaves = Array.isArray(pump.slaves) ? pump.slaves.map((s: any) => s.id || s) : [];
        const newSlaves = currentSlaves.filter((id: number) => id !== slaveId);
        saveSlaves(newSlaves, `removing-${slaveId}`);
    };

    const saveSlaves = (slaveIds: number[], actionKey: string) => {
        setIsSaving(actionKey);
        getApi().patch(`/bbe/userpumps/${id}/`, {
            slaves: slaveIds
        }).then((response: AxiosResponse) => {
            setPump(response.data);
            Swal.fire({
                title: 'Klart!',
                text: 'Slavpumparna har uppdaterats.',
                icon: 'success',
                timer: 1000,
                showConfirmButton: false,
                heightAuto: false
            });
        }).catch(() => {
            Swal.fire({
                title: 'Fel!',
                text: 'Kunde inte uppdatera slavpumpar.',
                icon: 'error',
                heightAuto: false
            });
        }).finally(() => {
            setIsSaving(null);
        });
    };

    const updateParameter = async (key: string, value: any) => {
        const label = getAlarmLabel(key);
        const isBool = typeof value === 'boolean';
        const isDisableAlarm = key.startsWith('disable_alarm');

        let confirmTitle = '';
        let confirmText = '';

        if (isBool) {
            const isActivating = isDisableAlarm ? (value === false) : (value === true);
            confirmTitle = isActivating ? `Aktivera ${label}?` : `Inaktivera ${label}?`;
            confirmText = isActivating ? `Vill du aktivera ${label.toLowerCase()}?` : `Vill du inaktivera ${label.toLowerCase()}?`;
        } else {
            confirmTitle = `Uppdatera ${label}?`;
            confirmText = `Vill du ändra värdet för ${label.toLowerCase()} till ${value}?`;
        }

        const result = await Swal.fire({
            title: confirmTitle,
            text: confirmText,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Ja, ändra',
            cancelButtonText: 'Avbryt',
            heightAuto: false
        });

        if (result.isConfirmed) {
            setIsSaving(key);
            try {
                await getApi().patch(`/bbe/userpumps/${id}/`, { [key]: value });
                setPump((prev: any) => ({ ...prev, [key]: value }));
                Swal.fire({
                    title: 'Sparat!',
                    text: 'Inställningen har uppdaterats.',
                    icon: 'success',
                    timer: 1500,
                    showConfirmButton: false,
                    heightAuto: false
                });
                if (!isBool) fetchData(); // Refresh data to make sure everything is in sync
            } catch (error) {
                Swal.fire({
                    title: 'Fel!',
                    text: 'Kunde inte spara inställningen.',
                    icon: 'error',
                    heightAuto: false
                });
            } finally {
                setIsSaving(null);
            }
        } else {
            // Om användaren avbryter, tvinga fram en omrendering för att toggles ska hoppa tillbaka
            if (isBool) {
                setPump((prev: any) => ({ ...prev }));
            }
        }
    };

    const updateAlarmSetting = (key: string, value: boolean) => updateParameter(key, value);

    const getAlarmLabel = (key: string) => {
        const labels: { [key: string]: string } = {
            disable_alarm_external: 'Externlarm',
            disable_alarm_flowguard: 'Flödesvakt',
            disable_alarm_lowguard: 'Lågtrycksvakt',
            disable_alarm_highguard: 'Högtrycksvakt',
            disable_alarm_pressuresensor: 'Tryckgivare',
            disable_alarm_max_pressure: 'Max tryck',
            disable_alarm_inverter: 'Frekvensomriktare',
            disable_alarm_start_signal: 'Startsignal',
            allowed_break_flowguard_time: 'Avbrottstid flödesvakt',
            allowed_break_lowguard_time: 'Avbrottstid lågtrycksvakt',
            allowed_break_highguard_time: 'Avbrottstid högtrycksvakt',
            allowed_break_maxpressure_time: 'Avbrottstid maxtrycksvakt',
            highguard: 'Högtrycksgräns',
            lowguard: 'Lågtrycksgräns',
            prestart_time: 'Förmatningstid',
            prestart_frequens: 'Förmatningsfrekvens',
            prestart_pressure_threshold: 'Trycktröskel förmatning',
            continuous_prefill: 'Kontinuerlig förmatning',
            flow_meter: 'Flödesmätare'
        };
        return labels[key] || key;
    };

    useIonViewWillEnter(() => {
        if (intervalRef.current) clearInterval(intervalRef.current);
        fetchData(true);
        intervalRef.current = setInterval(() => {
            fetchData();
        }, 10000);
    });

    useIonViewWillLeave(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
    });

    useEffect(() => {
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, []);

    return (
        <IonPage placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            <IonHeader placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                <IonToolbar color="primary" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                    <IonButtons slot="start" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                        <IonBackButton />
                    </IonButtons>
                    <IonTitle placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Parametrar PLC - {pump['name']}</IonTitle>
                    <div slot="end" style={{ paddingRight: '8px' }}><EnvironmentBadge /></div>
                </IonToolbar>
            </IonHeader>

            <IonContent fullscreen placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                <IonGrid placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                    <IonRow placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                        {/* Grunddata */}


                        {/* Larmhantering */}
                        <IonCol size="12" size-sm="6" size-md="3" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                            <IonCard placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                <IonCardHeader placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                    <IonCardTitle placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Larmhantering - Bygling</IonCardTitle>
                                </IonCardHeader>
                                <IonCardContent placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                    <div style={{ padding: '0 10px 10px', fontSize: '0.85em', color: '#666' }}>
                                        Här kan du inaktivera/by-passa specifika larmfunktioner i PLC:n.
                                    </div>
                                    <IonList placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                        {[
                                            'disable_alarm_external',
                                            'disable_alarm_flowguard',
                                            'disable_alarm_lowguard',
                                            'disable_alarm_highguard',
                                            'disable_alarm_pressuresensor',
                                            'disable_alarm_max_pressure',
                                            'disable_alarm_inverter',
                                            'disable_alarm_start_signal'
                                        ].map((key) => (
                                            <IonItem key={key} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                                <IonLabel placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                                    <h2 style={{ fontWeight: 'bold' }}>{getAlarmLabel(key)}</h2>
                                                    <p>{pump[key] ? 'Inaktiverat' : 'Aktivt'}</p>
                                                </IonLabel>
                                                {isSaving === key ? (
                                                    <IonSpinner slot="end" name="crescent" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                                                ) : (
                                                    <IonToggle
                                                        slot="end"
                                                        checked={!!pump[key]}
                                                        onIonChange={(e) => {
                                                            const val = e.detail.checked;
                                                            if (val !== !!pump[key]) {
                                                                updateAlarmSetting(key, val);
                                                            }
                                                        }}
                                                        placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}
                                                    />
                                                )}
                                            </IonItem>
                                        ))}
                                    </IonList>
                                </IonCardContent>
                            </IonCard>
                        </IonCol>

                        {/* Slavpumpar */}
                        <IonCol size="12" size-sm="6" size-md="3" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                            <IonCard placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                <IonCardHeader placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                    <IonCardTitle placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Slavpumpar</IonCardTitle>
                                </IonCardHeader>
                                <IonCardContent placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                    <IonList placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>

                                        <IonItem button onClick={() => setShowSlaveModal(true)} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                            <IonLabel placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                                <h2 style={{ fontWeight: 'bold' }}>Antal fjärrslavar</h2>
                                                <p>{Array.isArray(pump.slaves) ? pump.slaves.length : 0} st (Klicka för att hantera)</p>
                                            </IonLabel>
                                        </IonItem>
                                        {[
                                            { key: 'slave_1_start_frequency', label: 'Startfrekvens (Slav 1)', unit: 'Hz' },
                                            { key: 'slave_1_start_time', label: 'Startfördröjning (Slav 1)', unit: 'sekunder' },
                                            { key: 'slave_1_stop_frequency', label: 'Stoppfrekvens (Slav 1)', unit: 'Hz' },
                                            { key: 'slave_1_stop_time', label: 'Stoppfördröjning (Slav 1)', unit: 'sekunder' }
                                        ].map((item) => (
                                            <IonItem key={item.key} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                                <IonLabel position="stacked" style={{ fontWeight: 'bold', color: 'var(--ion-color-step-600)' }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                                    {item.label} ({item.unit})
                                                </IonLabel>
                                                <div style={{ display: 'flex', width: '100%', alignItems: 'center' }}>
                                                    <IonInput
                                                        type="number"
                                                        value={editValues[item.key]}
                                                        onIonChange={(e: any) => setEditValues(prev => ({ ...prev, [item.key]: e.detail.value! }))}
                                                        placeholder={`Ange ${item.unit}`}
                                                        onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}
                                                    />
                                                    <IonButton
                                                        fill="clear"
                                                        size="small"
                                                        onClick={() => updateParameter(item.key, parseFloat(editValues[item.key] || '0'))}
                                                        disabled={isSaving === item.key}
                                                        placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}
                                                    >
                                                        {isSaving === item.key ? <IonSpinner name="crescent" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} /> : 'Spara'}
                                                    </IonButton>
                                                </div>
                                            </IonItem>
                                        ))}
                                    </IonList>
                                </IonCardContent>
                            </IonCard>

                            {/* Flödesmätare */}
                            <IonCard placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                <IonCardHeader placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                    <IonCardTitle placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Flödesmätare</IonCardTitle>
                                </IonCardHeader>
                                <IonCardContent placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                    <IonList placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                        <IonItem placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                            <IonLabel placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                                <h2 style={{ fontWeight: 'bold' }}>Aktivera flödesmätare</h2>
                                                <p>{pump.flow_meter ? 'Aktiv' : 'Inaktiv'}</p>
                                            </IonLabel>
                                            {isSaving === 'flow_meter' ? (
                                                <IonSpinner slot="end" name="crescent" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                                            ) : (
                                                <IonToggle
                                                    slot="end"
                                                    checked={!!pump.flow_meter}
                                                    onIonChange={(e) => {
                                                        const val = e.detail.checked;
                                                        if (val !== !!pump.flow_meter) {
                                                            updateParameter('flow_meter', val);
                                                        }
                                                    }}
                                                    placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}
                                                />
                                            )}
                                        </IonItem>
                                    </IonList>
                                </IonCardContent>
                            </IonCard>
                        </IonCol>

                        {/* Flöde */}
                        <IonCol size="12" size-sm="6" size-md="3" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                            <IonCard placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                <IonCardHeader placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                    <IonCardTitle placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Flöde</IonCardTitle>
                                </IonCardHeader>
                                <IonCardContent placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                    <IonList placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                        <IonItem placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                            <IonLabel placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                                <h2 style={{ fontWeight: 'bold' }}>Minutflöde</h2>
                                                <p>{pump.flow_minute || 0} liter/min</p>
                                            </IonLabel>
                                        </IonItem>
                                        <IonItem placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                            <IonLabel placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                                <h2 style={{ fontWeight: 'bold' }}>Senaste mätning</h2>
                                                <p>{pump.flow_last_count || 0} pulser</p>
                                            </IonLabel>
                                        </IonItem>
                                        <IonItem placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                            <IonLabel placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                                <h2 style={{ fontWeight: 'bold' }}>Senast uppdaterad</h2>
                                                <p>{pump.flow_last_updated ? new Date(pump.flow_last_updated).toLocaleString('sv-SE') : 'Aldrig'}</p>
                                            </IonLabel>
                                        </IonItem>
                                    </IonList>
                                </IonCardContent>
                            </IonCard>

                            <IonCard placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                <IonCardHeader placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                    <IonCardTitle placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>SIM</IonCardTitle>
                                </IonCardHeader>
                                <IonCardContent placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                    <IonList placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                        <IonItem placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}><IonLabel placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}><b>IMSI:</b> {pump.imsi || 'Saknas'}</IonLabel></IonItem>
                                        <IonItem placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}><IonLabel placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}><b>ICCID:</b> {pump.iccid || 'Saknas'}</IonLabel></IonItem>
                                    </IonList>
                                </IonCardContent>
                            </IonCard>
                        </IonCol>

                        {/* Vakter */}
                        <IonCol size="12" size-sm="6" size-md="3" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                            <IonCard placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                <IonHeader placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                    <IonCardHeader placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                        <IonCardTitle placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Vakter</IonCardTitle>
                                    </IonCardHeader>
                                </IonHeader>
                                <IonCardContent placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                    <IonList placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                        {[
                                            { key: 'allowed_break_flowguard_time', label: 'Avbrottstid flödesvakt', unit: 'sek' },
                                            { key: 'allowed_break_lowguard_time', label: 'Avbrottstid lågtrycksvakt', unit: 'sek' },
                                            { key: 'allowed_break_highguard_time', label: 'Avbrottstid högtrycksvakt', unit: 'sek' },
                                            { key: 'allowed_break_maxpressure_time', label: 'Avbrottstid maxtrycksvakt', unit: 'sek' },
                                            { key: 'highguard', label: 'Högtrycksgräns', unit: 'bar' },
                                            { key: 'lowguard', label: 'Lågtrycksgräns', unit: 'bar' }
                                        ].map((item) => (
                                            <IonItem key={item.key} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                                <IonLabel position="stacked" style={{ fontWeight: 'bold', color: 'var(--ion-color-step-600)' }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                                    {item.label} ({item.unit})
                                                </IonLabel>
                                                <div style={{ display: 'flex', width: '100%', alignItems: 'center' }}>
                                                    <IonInput
                                                        type="number"
                                                        value={editValues[item.key]}
                                                        onIonChange={(e: any) => setEditValues(prev => ({ ...prev, [item.key]: e.detail.value! }))}
                                                        placeholder={`Ange ${item.unit}`}
                                                        onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}
                                                    />
                                                    <IonButton
                                                        fill="clear"
                                                        size="small"
                                                        onClick={() => updateParameter(item.key, parseFloat(editValues[item.key] || '0'))}
                                                        disabled={isSaving === item.key}
                                                        placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}
                                                    >
                                                        {isSaving === item.key ? <IonSpinner name="crescent" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} /> : 'Spara'}
                                                    </IonButton>
                                                </div>
                                            </IonItem>
                                        ))}
                                    </IonList>
                                </IonCardContent>
                            </IonCard>
                        </IonCol>

                        {/* Förmatning */}
                        <IonCol size="12" size-sm="6" size-md="3" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                            <IonCard placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                <IonCardHeader placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                    <IonCardTitle placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Förmatning</IonCardTitle>
                                </IonCardHeader>
                                <IonCardContent placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                    <IonList placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                        {[
                                            { key: 'prestart_time', label: 'Förmatningstid', unit: 'sek' },
                                            { key: 'prestart_frequens', label: 'Förmatningsfrekvens', unit: 'Hz' },
                                            { key: 'prestart_pressure_threshold', label: 'Trycktröskel', unit: 'bar' }
                                        ].map((item) => (
                                            <IonItem key={item.key} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                                <IonLabel position="stacked" style={{ fontWeight: 'bold', color: 'var(--ion-color-step-600)' }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                                    {item.label} ({item.unit})
                                                </IonLabel>
                                                <div style={{ display: 'flex', width: '100%', alignItems: 'center' }}>
                                                    <IonInput
                                                        type="number"
                                                        value={editValues[item.key]}
                                                        onIonChange={(e: any) => setEditValues(prev => ({ ...prev, [item.key]: e.detail.value! }))}
                                                        placeholder={`Ange ${item.unit}`}
                                                        onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}
                                                    />
                                                    <IonButton
                                                        fill="clear"
                                                        size="small"
                                                        onClick={() => updateParameter(item.key, parseFloat(editValues[item.key] || '0'))}
                                                        disabled={isSaving === item.key}
                                                        placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}
                                                    >
                                                        {isSaving === item.key ? <IonSpinner name="crescent" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} /> : 'Spara'}
                                                    </IonButton>
                                                </div>
                                            </IonItem>
                                        ))}
                                        <IonItem placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                            <IonLabel placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                                <h2 style={{ fontWeight: 'bold' }}>Kontinuerlig förmatning</h2>
                                                <p>{pump.continuous_prefill ? 'Aktiv' : 'Inaktiv'}</p>
                                            </IonLabel>
                                            {isSaving === 'continuous_prefill' ? (
                                                <IonSpinner slot="end" name="crescent" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                                            ) : (
                                                <IonToggle
                                                    slot="end"
                                                    checked={!!pump.continuous_prefill}
                                                    onIonChange={(e) => {
                                                        const val = e.detail.checked;
                                                        if (val !== !!pump.continuous_prefill) {
                                                            updateParameter('continuous_prefill', val);
                                                        }
                                                    }}
                                                    placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}
                                                />
                                            )}
                                        </IonItem>
                                    </IonList>
                                </IonCardContent>
                            </IonCard>
                        </IonCol>

                    </IonRow>
                </IonGrid>
            </IonContent>

            <IonModal isOpen={showSlaveModal} onDidDismiss={() => setShowSlaveModal(false)} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                <IonHeader placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                    <IonToolbar color="primary" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                        <IonTitle placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Hantera fjärrslavar</IonTitle>
                        <IonButtons slot="end" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                            <IonButton onClick={() => setShowSlaveModal(false)} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                <IonIcon icon={closeOutline} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                            </IonButton>
                        </IonButtons>
                    </IonToolbar>
                </IonHeader>
                <IonContent placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                    <div style={{ padding: '16px' }}>
                        <h3 style={{ margin: '0 0 8px', fontSize: '1.2em', fontWeight: 'bold' }}>Inlagda fjärrslavar</h3>
                        <IonList placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                            {Array.isArray(pump.slaves) && pump.slaves.length > 0 ? (
                                pump.slaves.map((slave: any) => (
                                    <IonItem key={slave.id} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                        <IonLabel placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>{slave.name}</IonLabel>
                                        <IonButton fill="clear" color="danger" slot="end" onClick={() => removeSlave(slave.id)} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                            <IonIcon icon={trashOutline} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                                        </IonButton>
                                    </IonItem>
                                ))
                            ) : (
                                <IonItem lines="none" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}><IonLabel color="medium" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Inga slavar tillagda</IonLabel></IonItem>
                            )}
                        </IonList>

                        <h3 style={{ margin: '24px 0 8px', fontSize: '1.2em', fontWeight: 'bold' }}>Lägg till fjärrslav</h3>
                        <IonSearchbar
                            onIonChange={(e: any) => setSearchText(e.detail.value!)}
                            debounce={500}
                            placeholder="Sök efter pump..."
                            onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}
                        />
                        <IonList placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                            {allPumps
                                .filter((p: any) =>
                                    p.id !== parseInt(id) &&
                                    !(Array.isArray(pump.slaves) && pump.slaves.some((s: any) => (s.id || s) === p.id)) &&
                                    (p.name?.toLowerCase().includes(searchText.toLowerCase()) || searchText === '')
                                )
                                .slice(0, 5)
                                .map((p: any) => (
                                    <IonItem key={p.id} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                        <IonLabel placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                            <h2 style={{ fontWeight: 'bold' }}>{p.name}</h2>
                                            <p>{p.status}</p>
                                        </IonLabel>
                                        <IonButton
                                            fill="outline"
                                            slot="end"
                                            onClick={() => addSlave(p.id)}
                                            disabled={isSaving !== null}
                                            placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}
                                        >
                                            {isSaving === `adding-${p.id}` ? (
                                                <IonSpinner name="crescent" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                                            ) : (
                                                <>
                                                    <IonIcon icon={addCircleOutline} slot="start" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                                                    Lägg till
                                                </>
                                            )}
                                        </IonButton>
                                    </IonItem>
                                ))}
                        </IonList>
                    </div>
                </IonContent>
            </IonModal>

        </IonPage>
    );
};

export default PLCParameters;

